import { Link } from 'react-router-dom';
import { usePlanAccess } from '@/hooks/usePlanAccess';

interface TrialBannerProps {
  variant?: 'dashboard' | 'subscription';
}

export default function TrialBanner({ variant = 'dashboard' }: TrialBannerProps) {
  const { isTrial, trialDaysRemaining, trialExpired } = usePlanAccess();

  if (!isTrial) return null;

  if (trialExpired) {
    return (
      <div className="bg-red-50 dark:bg-red-500/8 border border-red-200 dark:border-red-500/15 rounded-2xl p-5 animate-fadeIn">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-500/15 flex items-center justify-center flex-shrink-0">
              <i className="ri-timer-flash-line text-xl text-red-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                Seu período de teste gratuito expirou!
              </p>
              <p className="text-xs text-red-600 dark:text-red-500 mt-0.5">
                Escolha um plano para continuar usando o WZap PRO.
              </p>
            </div>
          </div>
          <Link
            to="/subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            <i className="ri-vip-crown-line" />
            Escolher Plano
          </Link>
        </div>
      </div>
    );
  }

  const daysText = trialDaysRemaining === 1 ? '1 dia' : `${trialDaysRemaining} dias`;
  const expiringSoon = trialDaysRemaining !== null && trialDaysRemaining <= 3;

  if (expiringSoon) {
    // Urgent banner for 3 days or less remaining
    return (
      <div className="bg-gradient-to-r from-red-50 via-orange-50 to-amber-50 dark:from-red-500/10 dark:via-orange-500/10 dark:to-amber-500/10 border border-red-200 dark:border-red-500/20 rounded-2xl p-5 animate-fadeIn">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-500/15 flex items-center justify-center flex-shrink-0 animate-pulse">
              <i className="ri-alarm-warning-line text-xl text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                Seu teste grátis acaba em <span className="text-red-600 dark:text-red-400">{daysText}</span>!
              </p>
              <p className="text-xs text-red-700 dark:text-red-500 mt-0.5">
                Não perca seus dados e configurações. Faça upgrade agora e garanta a continuidade.
              </p>
            </div>
          </div>
          <Link
            to="/subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 shadow-sm hover:shadow-md active:scale-[0.98]"
          >
            <i className="ri-arrow-up-circle-line" />
            Fazer Upgrade Agora
          </Link>
        </div>
      </div>
    );
  }

  if (variant === 'subscription') {
    return (
      <div className="bg-gradient-to-r from-primary-50 via-primary-50/70 to-accent-50 dark:from-primary-500/5 dark:via-primary-500/3 dark:to-accent-500/5 border border-primary-200 dark:border-primary-500/15 rounded-2xl p-5 animate-fadeIn">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-500/15 flex items-center justify-center flex-shrink-0">
              <i className="ri-gift-line text-xl text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">
                Você está no plano Trial — <span className="text-primary-600 dark:text-primary-400">{daysText} restantes</span>
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">
                Até 500 mensagens, 1 WhatsApp, 1 usuário. Faça upgrade para liberar tudo!
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 rounded-full text-xs font-semibold whitespace-nowrap">
            <i className="ri-time-line" />
            {daysText}
          </span>
        </div>
      </div>
    );
  }

  // Dashboard variant
  return (
    <div className="bg-gradient-to-r from-primary-50 via-primary-50/70 to-accent-50 dark:from-primary-500/5 dark:via-primary-500/3 dark:to-accent-500/5 border border-primary-200 dark:border-primary-500/15 rounded-2xl p-4 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-500/15 flex items-center justify-center flex-shrink-0">
            <i className="ri-gift-line text-lg text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">
              Teste grátis — <span className="text-primary-600 dark:text-primary-400">{daysText} restantes</span>
            </p>
            <p className="text-xs text-foreground-500">
              Até 500 mensagens. Faça upgrade e libere todos os recursos!
            </p>
          </div>
        </div>
        <Link
          to="/subscription"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-vip-crown-line" />
          Ver Planos
        </Link>
      </div>
    </div>
  );
}